import { useUrlGenerator } from '@folklore/routes';
import { useTransition, animated } from '@react-spring/web';
import classNames from 'classnames';
import { differenceInSeconds, parseISO } from 'date-fns';
// import PropTypes from 'prop-types';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'wouter';

import { useAnswerQuestion } from '../../../hooks/useGame';
import * as AppPropTypes from '../../../lib/PropTypes';
import { getComponentFromName } from '../../../lib/utils';

import { useGame, useParticipation } from '../../../contexts/GameContext';
import { useThemeClassName } from '../../../contexts/ThemeContext';
import * as FeedbackComponents from '../../animations/feedbacks';
import * as IntrosComponents from '../../interactive/intros';
import Body from '../../partials/Body';
import GameTimer from '../../partials/GameTimer';
import Choices from '../../questions/Choices';
import GameEnd from '../GameEnd';

import styles from '../../../../styles/games/exam/question-view.module.scss';

const propTypes = {
    questionsGroup: AppPropTypes.questionsGroup,
    question: AppPropTypes.question,
};

const defaultProps = {
    questionsGroup: null,
    question: null,
};

function ExamQuestionView({ questionsGroup, question }) {
    const route = useUrlGenerator();
    const [, navigate] = useLocation();
    const [introVisible, setIntroVisible] = useState(false);
    const [ended, setEnded] = useState(false);

    const { id: currentQuestionsGroupId, questions: questionsOfCurrentGroup = null } =
        questionsGroup;
    const {
        id: questionId = null,
        body: questionBody = null,
        image: questionImage = null,
        points = null,
        duration: questionDuration = null,
        explanation: questionExplanation = null,
    } = question;
    const { url: questionImageUrl = null } = questionImage || {};

    const game = useGame();
    const { id: gameId = null, title: gameTitle = null, theme: gameTheme = null } = game || {};
    const {
        intro: introComponent = null,
        feedback: feedbackComponent = null,
        feedbackValid,
        feedbackInvalid,
    } = gameTheme || {};
    const IntroComponent = getComponentFromName(IntrosComponents, introComponent);
    const FeedbackComponent = getComponentFromName(FeedbackComponents, feedbackComponent);

    const participation = useParticipation() || {};
    const { answers: participationAnswers = [] } = participation;

    const participationAnswer = useMemo(
        () => participationAnswers.find((a) => a.question === questionId) || null,
        [participationAnswers, questionId],
    );
    const {
        valid = null,
        value: participationAnswerValue = null,
        createdAt: participationCreatedAt = null,
        validatedAt: participationValidatedAt = null,
    } = participationAnswer || {};
    const [feedback, setFeedback] = useState(valid);

    // can we derive this from participationAnswer?
    const answerPoints = valid === true ? points : 0;

    const questionsCount = questionsOfCurrentGroup.length;
    const currentQuestionIndex = useMemo(
        () => questionsOfCurrentGroup.findIndex((q) => q.id === question.id) || 0,
        [question],
    );

    const { answerQuestionAsync, isMutating: loading } = useAnswerQuestion();

    const postAnswer = useCallback(
        (value) => {
            answerQuestionAsync({
                questionId,
                answer: value,
            }).then(({ participation: updatedParticipation }) => {
                const { answers: updatedParticipationAnswers } = updatedParticipation;
                const { valid: updatedValid = null } =
                    updatedParticipationAnswers.find((a) => a.question === questionId) || null;
                if (updatedValid !== null) {
                    setTimeout(() => setFeedback(updatedValid), 1000);
                }
            });
        },
        [
            questionsOfCurrentGroup,
            currentQuestionIndex,
            gameId,
            questionId,
            currentQuestionsGroupId,
        ],
    );

    const onTimerComplete = useCallback(() => {
        postAnswer(null);
    }, [postAnswer]);

    const goToNextQuestion = useCallback(() => {
        setFeedback(null);
        const nextQuestionIndex = currentQuestionIndex + 1;
        const { id: nextQuestionId = null } = questionsOfCurrentGroup[nextQuestionIndex] || {};
        if (nextQuestionId !== null) {
            navigate(
                route('game.question', {
                    game: gameId,
                    question: nextQuestionId,
                }),
            );
        } else {
            setEnded(true);
        }
    }, [currentQuestionIndex, questionsCount, gameId, questionsOfCurrentGroup]);

    const startQuestion = useCallback(() => {
        if (participationAnswer === null) {
            postAnswer(null);
        }
    }, [participationAnswer]);

    const onIntroComplete = useCallback(() => {
        setIntroVisible(false);
        startQuestion();
    }, [startQuestion]);

    useEffect(() => {
        const isFirstQuestion = currentQuestionIndex === 0;

        if (isFirstQuestion && participationAnswer === null && IntroComponent !== null) {
            setIntroVisible(true);
        } else {
            startQuestion();
        }
    }, [currentQuestionIndex, participationAnswer, IntroComponent]);

    const now = new Date();
    const timerDuration = useMemo(() => {
        if (participationCreatedAt === null) {
            return questionDuration;
        }

        const createdDiff = differenceInSeconds(now, parseISO(participationCreatedAt));
        return Math.max(0, questionDuration - createdDiff);
    }, [now, participationCreatedAt]);

    const answerDuration = useMemo(() => {
        if (participationValidatedAt !== null) {
            return differenceInSeconds(
                parseISO(participationValidatedAt),
                parseISO(participationCreatedAt),
            );
        }
        return timerDuration;
    }, [participationCreatedAt, participationValidatedAt, timerDuration]);

    const validAnswerText = useMemo(() => {
        const { validAnswer = null } = question;
        if (validAnswer === null) {
            return null;
        }

        const { label = null } = question.choices.find((c) => c.id === validAnswer) || {};
        return label;
    }, [question]);

    const themeClassName = useThemeClassName(styles);
    const transitions = useTransition(ended, {
        initial: { opacity: 1 },
        from: { opacity: 0 },
        enter: { opacity: 1 },
        leave: { opacity: 0 },
    });

    return transitions((style, isEndedView) => {
        if (isEndedView) {
            return (
                <animated.div style={style}>
                    <GameEnd
                        game={game}
                        participation={participation}
                        questionsGroup={questionsGroup}
                        withoutAnswersCount
                        className={styles.end}
                    />
                </animated.div>
            );
        }

        return (
            <animated.div style={style}>
                <div className={classNames([styles.container, themeClassName])}>
                    <div className={styles.header}>
                        <div className={styles.infoBlock}>
                            <div className={styles.title}>{gameTitle}</div>
                            <div className={styles.info}>
                                <div className={styles.index}>
                                    {currentQuestionIndex + 1}/{questionsCount}
                                </div>
                                {points !== null && (
                                    <div className={styles.points}>{points} pts</div>
                                )}
                            </div>
                        </div>
                        <GameTimer
                            className={styles.timer}
                            duration={timerDuration}
                            started={!introVisible && valid === null}
                            onComplete={onTimerComplete}
                        />
                    </div>

                    <div className={styles.question}>
                        {/* eslint-disable-next-line react/no-danger */}
                        <Body body={questionBody} />
                        {questionImageUrl && (
                            <img className={styles.image} src={questionImageUrl} alt="question" />
                        )}
                    </div>

                    <div className={styles.inner}>
                        <Choices
                            className={styles.choices}
                            question={question}
                            isShuffled={false}
                            answer={participationAnswer}
                            onAnswer={postAnswer}
                            disabled={loading}
                        />
                    </div>

                    {FeedbackComponent !== null ? (
                        <FeedbackComponent
                            className={classNames([
                                styles.feedback,
                                {
                                    [styles.isActive]: feedback !== null,
                                },
                            ])}
                            valid={feedback}
                            value={participationAnswerValue}
                            validLabel={feedbackValid}
                            invalidLabel={feedbackInvalid}
                            answerDuration={answerDuration}
                            answerPoints={answerPoints}
                            validAnswerText={validAnswerText}
                            explanation={questionExplanation}
                            gameId={gameId}
                            questionsGroupId={currentQuestionsGroupId}
                            onAnimationComplete={goToNextQuestion}
                        />
                    ) : null}

                    {introVisible && IntroComponent !== null ? (
                        <IntroComponent onClose={onIntroComplete} />
                    ) : null}
                </div>
            </animated.div>
        );
    });
}

ExamQuestionView.propTypes = propTypes;
ExamQuestionView.defaultProps = defaultProps;

export default ExamQuestionView;
